/**
 * Canonical country list — the single source for the Country combobox across the
 * intake (About-you country + per-person place-of-birth country).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS FILE EXISTS (L1A intake redesign, 2026-06-21)
 *    The redesign replaces the free-typed country `<input>` (and the native
 *    `<datalist>`, which renders white-on-white on some platforms) with a custom
 *    type-to-pick combobox over a FIXED canonical list — no typos, stable values
 *    that downstream region logic (remedy links) can key on.
 *
 *    The list is ISO-style common-English country NAMES (not codes) — the form
 *    stores the NAME (matching the legacy `answers.country` free-string), so a
 *    pre-redesign doc that already stored "India" round-trips unchanged. India is
 *    first (the overwhelming default), then the rest alphabetical.
 *
 *  PURE — no Firebase / React import, so the Node `.cjs` test runner loads it via
 *  `_tsload.cjs` with zero side effects.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * The canonical country list. India first (default), then alphabetical. The
 * value stored on the form/doc is the string itself (back-compat with the legacy
 * free-text `answers.country`).
 */
export const COUNTRIES: readonly string[] = [
  'India',
  'Afghanistan',
  'Australia',
  'Bahrain',
  'Bangladesh',
  'Bhutan',
  'Brazil',
  'Canada',
  'China',
  'Egypt',
  'France',
  'Germany',
  'Hong Kong',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Italy',
  'Japan',
  'Jordan',
  'Kenya',
  'Kuwait',
  'Malaysia',
  'Maldives',
  'Mauritius',
  'Myanmar',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Oman',
  'Pakistan',
  'Philippines',
  'Qatar',
  'Russia',
  'Saudi Arabia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Spain',
  'Sri Lanka',
  'Sweden',
  'Switzerland',
  'Thailand',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Vietnam',
] as const;

/**
 * International dialling (calling) code per canonical country — the SSOT for the
 * dial-code selector on any phone-with-country-code input (e.g. the public Door B
 * booking form, so WhatsApp confirmations get a full E.164 number). Keyed by the
 * exact `COUNTRIES` name. India first by list order; the NANP entries (Canada /
 * United States) legitimately share `+1`.
 */
export const COUNTRY_DIAL_CODES: Readonly<Record<string, string>> = {
  India: '+91',
  Afghanistan: '+93',
  Australia: '+61',
  Bahrain: '+973',
  Bangladesh: '+880',
  Bhutan: '+975',
  Brazil: '+55',
  Canada: '+1',
  China: '+86',
  Egypt: '+20',
  France: '+33',
  Germany: '+49',
  'Hong Kong': '+852',
  Indonesia: '+62',
  Iran: '+98',
  Iraq: '+964',
  Ireland: '+353',
  Italy: '+39',
  Japan: '+81',
  Jordan: '+962',
  Kenya: '+254',
  Kuwait: '+965',
  Malaysia: '+60',
  Maldives: '+960',
  Mauritius: '+230',
  Myanmar: '+95',
  Nepal: '+977',
  Netherlands: '+31',
  'New Zealand': '+64',
  Nigeria: '+234',
  Oman: '+968',
  Pakistan: '+92',
  Philippines: '+63',
  Qatar: '+974',
  Russia: '+7',
  'Saudi Arabia': '+966',
  Singapore: '+65',
  'South Africa': '+27',
  'South Korea': '+82',
  Spain: '+34',
  'Sri Lanka': '+94',
  Sweden: '+46',
  Switzerland: '+41',
  Thailand: '+66',
  'United Arab Emirates': '+971',
  'United Kingdom': '+44',
  'United States': '+1',
  Vietnam: '+84',
};

/** One dial-code option for a country-calling-code selector. */
export interface DialOption {
  /** Canonical country name (the stored value). */
  name: string;
  /** Calling code, e.g. `+91`. */
  dial: string;
}

/** The dial-code selector options, India first then alphabetical (COUNTRIES order). */
export const DIAL_OPTIONS: readonly DialOption[] = COUNTRIES.map((name) => ({
  name,
  dial: COUNTRY_DIAL_CODES[name] ?? '',
}));

/** A valid E.164 number: `+`, a non-zero leading digit, total 8–15 digits. */
const E164_RE = /^\+[1-9]\d{7,14}$/;

/** PURE — is this a syntactically valid E.164 number? */
export function isValidE164(value: string): boolean {
  return E164_RE.test((value || '').trim());
}

/**
 * PURE — compose a full E.164 number from a dial code + a national number.
 *
 * - If the national field already starts with `+`, the user typed a full
 *   international number → we normalise THAT (the dial select is ignored), so a
 *   pasted `+15879262244` is respected.
 * - Otherwise we strip the national number to digits, drop a leading trunk `0`,
 *   and prefix the selected dial code → `+<country><national>`.
 *
 * Returns the E.164 string, or `null` when the result isn't a valid E.164 (empty
 * / too short / too long) so the caller can show a clear validation message.
 */
export function toE164(dial: string, national: string): string | null {
  const raw = (national || '').trim();
  if (raw.startsWith('+')) {
    const full = '+' + raw.replace(/[^\d]/g, '');
    return isValidE164(full) ? full : null;
  }
  const dialDigits = (dial || '').replace(/[^\d]/g, '');
  if (!dialDigits) return null;
  const nat = raw.replace(/[^\d]/g, '').replace(/^0+/, '');
  if (!nat) return null;
  const full = '+' + dialDigits + nat;
  return isValidE164(full) ? full : null;
}

/**
 * PURE — best-effort split of a full E.164 (or `+`-prefixed) number back into a
 * canonical country name + national remainder, for pre-filling the two controls
 * from a stored number. Longest dial-code match wins (so `+1` vs `+91` resolve
 * correctly). Falls back to India when the leading code isn't recognised.
 */
export function splitE164(value: string): { country: string; national: string } {
  const raw = (value || '').trim();
  if (!raw.startsWith('+')) return { country: 'India', national: raw };
  const digits = raw.replace(/[^\d]/g, '');
  let best: { country: string; codeLen: number } | null = null;
  for (const { name, dial } of DIAL_OPTIONS) {
    const code = dial.replace(/[^\d]/g, '');
    if (code && digits.startsWith(code)) {
      if (!best || code.length > best.codeLen) best = { country: name, codeLen: code.length };
    }
  }
  if (!best) return { country: 'India', national: digits };
  return { country: best.country, national: digits.slice(best.codeLen) };
}

/** A Set for O(1) membership tests (case-sensitive canonical match). */
const COUNTRY_SET = new Set<string>(COUNTRIES);

/** PURE — is this exact string one of the canonical country names? */
export function isCanonicalCountry(name: string): boolean {
  return COUNTRY_SET.has((name || '').trim());
}

/**
 * PURE — filter the canonical list by a free-typed query (case-insensitive
 * substring). An empty query returns the whole list (India first). Used by the
 * combobox to render its dropdown.
 */
export function searchCountries(query: string): string[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [...COUNTRIES];
  return COUNTRIES.filter((c) => c.toLowerCase().includes(q));
}
